import { PerspectiveCamera } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { AssetVizMode } from 'features/assetViz/defines'
import { MouseHandler } from 'features/assetViz/systems/MouseHandler'
import {
  CameraBounds,
  CameraCoords,
  Vec2,
  Vec3,
} from 'features/assetViz/utils/helpers'
import { RenderLayerID } from 'features/assetViz/utils/renderingDefines'
import React from 'react'
import * as THREE from 'three'
import { GeckoComponentRect } from 'types'

const FAR_CLIP = 12 * 10 * 1000
const NEAR_CLIP = 0.25

//------------------------------------------------------------------------------

export type SelectionCallback = (
  componentID: string
) => GeckoComponentRect | undefined
export type BoundsCalculator = (componentID: string) => CameraBounds | undefined
export type PositionCalculator = (cameraCoords: CameraCoords) => {
  position: Vec3
  rotation: Vec3
  smoothMovement?: boolean
}
export type MovementCalculator = (cursorDelta: Vec2) => Vec2
export type FitCalculator = (dimensions: GeckoComponentRect) => Vec2

//------------------------------------------------------------------------------

/**
 * A camera which looks directly at (i.e. normal to) a single component's
 * surface, used as the default in most scenes. This camera can be easily
 * configured via callbacks to match any desired geometry, and preserves its
 * state across scenes (for example, when switching back and forth between
 * 2D and 3D views)
 * @param onComponentSelected       A callback for when a new component is selected
 * @param getCameraBounds           A callback to limit camera movement
 * @param getCameraPosition         A callback to calculate camera position
 * @param getMovementForCursorDelta An optional callback to determine how much
 *                                  to move the camera (in surface coordinates)
 *                                  given mouse movement
 * @param getCameraDimensionsFit    An optional callback to calculate how wide
 *                                  and tall of a surface bounding box to try
 *                                  and fit on-screen. If none is provided, we'll
 *                                  fit the whole thing, just like a plane
 */
export const ShipHullCamera3D: React.FC<{
  getMovementForCursorDelta: MovementCalculator
}> = ({ getMovementForCursorDelta }) => {
  //----------------------------------------------------------------------------

  const { focusedComponentID, mode } = useAssetVizInternal()

  const { camera } = useThree()

  // Camera coordinates described in terms of component x and y which persist
  // across different scenes. Note that we don't want to call overrideCameraCoords()
  // very often, it's better to update this object in place since we're accessing
  // this object each frame, rather than on React's refresh cycle
  const [cameraCoords, overrideCameraCoords] = React.useState<CameraCoords>()

  const ref = React.useRef<THREE.PerspectiveCamera>(null)
  const snapToNewPositionRef = React.useRef(false)
  const [hasInitialized, setHasInitialized] = React.useState(false)

  React.useEffect(() => {
    // Otherwise, if this scene hasn't been initialized yet, initialize it
    if (!hasInitialized) {
      ref.current?.layers.disableAll()
      ref.current?.layers.enable(RenderLayerID.Default)
      ref.current?.layers.enable(RenderLayerID.VisibleCollidable)
      ref.current?.layers.enable(RenderLayerID.VisibleNonCollidable)
      overrideCameraCoords({
        componentID: focusedComponentID,
        componentX: camera.position.x,
        componentY: camera.position.y,
        distToComponent: camera.position.z,
      })
      setHasInitialized(true)
    }
    snapToNewPositionRef.current = true
  }, [
    camera.position.x,
    camera.position.y,
    camera.position.z,
    focusedComponentID,
    hasInitialized,
  ])

  // Frame loop ----------------------------------------------------------------

  useFrame(() => {
    if (!cameraCoords) return

    // Camera movement ---------------------------------------------------------

    if (ref.current) {
      overrideCameraCoords({
        componentID: focusedComponentID,
        componentX: camera.position.x,
        componentY: camera.position.y,
        distToComponent: camera.position.z,
      })
    }
  })

  //----------------------------------------------------------------------------

  return (
    <>
      <PerspectiveCamera
        makeDefault
        ref={ref}
        fov={50}
        near={NEAR_CLIP}
        far={FAR_CLIP}
        // Set the starting position way outside the bounds of the scene for
        // the first frame to prevent an awkward first render at [0, 0, 0]
        position={[1e20, 1e20, 1e20]}
      />
      {!cameraCoords || mode === AssetVizMode.NoInteraction ? null : (
        <MouseHandler
          cameraCoords={cameraCoords}
          getMovementForCursorDelta={getMovementForCursorDelta ?? ((xy) => xy)}
        />
      )}
    </>
  )
}
